// 動画再生、hlsURL取得、再生時間の計算などを担う。
import { range } from 'lodash-es';
import validator from 'validator';
import { observable, makeObservable } from 'mobx';
import { isMobileSafari, isiOSApp } from '@lean-body/src/util';
export var VideoType;
(function (VideoType) {
    VideoType["ProgramIntroduce"] = "program_introduce";
    VideoType["Lesson"] = "lesson";
})(VideoType || (VideoType = {}));
/**
 * 動画再生状態のステータス
 */
export var VideoState;
(function (VideoState) {
    VideoState[VideoState["Loading"] = 0] = "Loading";
    VideoState[VideoState["Ready"] = 1] = "Ready";
    VideoState[VideoState["Playing"] = 2] = "Playing";
    VideoState[VideoState["Quit"] = 3] = "Quit";
    VideoState[VideoState["Result"] = 4] = "Result";
    VideoState[VideoState["Finished"] = 5] = "Finished";
})(VideoState || (VideoState = {}));
/**
 * TODO: ほぼ使われていないためVideoStateと統合する
 * 動画プレイヤーのステータス
 */
export var VideoPlayerStatus;
(function (VideoPlayerStatus) {
    VideoPlayerStatus[VideoPlayerStatus["Waiting"] = 0] = "Waiting";
    VideoPlayerStatus[VideoPlayerStatus["Ready"] = 1] = "Ready";
    VideoPlayerStatus[VideoPlayerStatus["Playing"] = 2] = "Playing";
    VideoPlayerStatus[VideoPlayerStatus["Paused"] = 3] = "Paused";
    VideoPlayerStatus[VideoPlayerStatus["Seeking"] = 4] = "Seeking";
    VideoPlayerStatus[VideoPlayerStatus["Finished"] = 5] = "Finished";
})(VideoPlayerStatus || (VideoPlayerStatus = {}));
export class VideoSrc {
    constructor(rawPath, config) {
        Object.defineProperty(this, "rawPath", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.config = config;
        this.rawPath = rawPath;
    }
    get isValid() {
        if (validator.isEmpty(this.rawPath)) {
            return false;
        }
        return true;
    }
    get id() {
        return this.rawPath;
    }
    get hlsURL() {
        return this.config.cdnBaseForMovie + this.hlsPath;
    }
    get hlsPath() {
        if (this.rawPath.includes('.mov')) {
            return this.rawPath.replace('.mov', '') + '/master.m3u8';
        }
        return this.rawPath.replace('.mp4', '') + '/master.m3u8';
    }
}
/*=============================================
= Video Events =
=============================================*/
export class VideoEvent {
    constructor(time) {
        Object.defineProperty(this, "time", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.time = time;
    }
}
export class VideoSeekedEvent extends VideoEvent {
    constructor(range) {
        super(range.to);
        Object.defineProperty(this, "range", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.range = range;
    }
}
export class VideoPlayEvent extends VideoEvent {
    constructor(range) {
        super(range.to);
        Object.defineProperty(this, "range", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.range = range;
    }
}
/*=============================================
= Domain Models =
=============================================*/
/**
 * HTMLVideoElementのイベントをハンドルし、
 * VideoVM、VideoPlayerContext、VideoPlayAnalyzerの操作を行う
 */
export class VideoElementEventHandler {
    constructor(videoVM, playerCtx, analyzer) {
        Object.defineProperty(this, "videoVM", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "playerCtx", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "analyzer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.videoVM = videoVM;
        this.playerCtx = playerCtx;
        this.analyzer = analyzer;
    }
    setAnalyzer(analyzer) {
        this.analyzer = analyzer;
    }
    initialize(el) {
        el.addEventListener('ended', () => {
            this.videoVM.onLessonComplete();
            this.playerCtx.setFinished();
            this.analyzer.onCompleted();
        });
        el.addEventListener('pause', () => {
            // シーク時、終了時もpauseイベントが飛んでくるのでそれらを除外
            if (!el.seeking && el.currentTime < el.duration) {
                this.videoVM.onLessonSuspend();
                // HACK: iOS Safari は FullscreenAPI が非対応のため、フルスクリーンの解除をした場合に発火される動画停止のイベントをハンドリングしてフルスクリーン解除時の後処理をします
                if (isMobileSafari() || isiOSApp()) {
                    this.videoVM.onFullscreenCancellation();
                }
                this.playerCtx.setPaused();
                this.analyzer.onPaused();
            }
        });
        el.addEventListener('seeking', () => {
            this.playerCtx.setSeeking();
        });
        el.addEventListener('seeked', () => {
            this.videoVM.onLessonSeeked(el.currentTime);
            this.playerCtx.setSeeking();
            this.playerCtx.setCurrentTime(el.currentTime);
            const range = this.playerCtx.getSeekRange();
            this.playerCtx.setPlaying();
            if (range) {
                // 再生開始前(VideoPlayerStatusがreadyやwaitingの時に)seeking、seekedが呼ばれることがあり、
                // このときはrange = null
                const event = new VideoSeekedEvent(range);
                this.analyzer.onSeeked(event);
            }
        });
        el.addEventListener('playing', () => {
            this.videoVM.onClickPlayButton().then(() => {
                this.playerCtx.setPlaying();
                this.playerCtx.setCurrentTime(el.currentTime);
            });
        });
        el.addEventListener('timeupdate', () => {
            const newCurrentTime = el.currentTime;
            const lastPlayedTime = this.playerCtx.currentTime;
            const lapsed = newCurrentTime - this.playerCtx.currentTime;
            switch (this.playerCtx.status) {
                case VideoPlayerStatus.Playing:
                    // イベントフィルタ
                    if (lapsed < 0 || lapsed > 1) {
                        // シーク時、一時停止時に seeking, pause, seeked イベントより前にtimeupdate イベントが発行される場合がある
                        // つまり status = playing の状態で、シークに伴うtimeupdateが呼ばれる場合があるで、
                        // 再生中のときのみイベントハンドルするようフィルタしたい。
                        // 再生中にtimeupdateイベントは250ms以内に発行されるため、
                        // 前回のcurrentTimeとの差分が0未満または(余裕を持って)1sec以上だった場合は、
                        // シークに伴うtimeupdateとみなしてハンドルしない
                        return;
                    }
                    // timeupdate イベントがseekedより前に発行された場合、
                    // playerCtx.setCurrentTime() をイベントフィルタ前に呼ぶと、
                    // seekedイベントのハンドルでシーク範囲を算出できなくなるためここで呼ぶ。
                    this.playerCtx.setCurrentTime(el.currentTime);
                    this.videoVM.onTimeUpdate(this.playerCtx.currentTime);
                    this.analyzer.onPlaying(new VideoPlayEvent({
                        from: lastPlayedTime,
                        to: this.playerCtx.currentTime,
                    }));
                    break;
            }
        });
    }
}
/**
 * 動画再生状態のコンテキスト
 */
export class VideoPlayerContext {
    constructor() {
        Object.defineProperty(this, "status", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: VideoPlayerStatus.Waiting
        });
        Object.defineProperty(this, "currentTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "seekFromTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        makeObservable(this, {
            status: observable,
        });
    }
    setStatus(status) {
        this.status = status;
    }
    /**
     * 再生可能状態へ遷移
     */
    setReady() {
        this.setStatus(VideoPlayerStatus.Ready);
    }
    /**
     * シーク中状態へ遷移
     * 再生中、停止中からのみシーク中へ遷移可能
     * 状態遷移時に `currentTime` をシーク元時刻 seekFromTime として保持
     */
    setSeeking() {
        switch (this.status) {
            case VideoPlayerStatus.Playing:
            case VideoPlayerStatus.Paused:
                this.setStatus(VideoPlayerStatus.Seeking);
                this.seekFromTime = this.currentTime;
                break;
            default:
                break;
        }
    }
    /**
     * シーク中のときシーク範囲を取得する
     */
    getSeekRange() {
        switch (this.status) {
            case VideoPlayerStatus.Seeking:
                return { from: this.seekFromTime, to: this.currentTime };
            default:
                return null;
        }
    }
    /**
     * ポーズ状態へ遷移
     * 再生中、ポーズ中、シーク中からのみ遷移可能
     */
    setPaused() {
        switch (this.status) {
            case VideoPlayerStatus.Playing:
            case VideoPlayerStatus.Paused:
            case VideoPlayerStatus.Seeking:
                this.setStatus(VideoPlayerStatus.Paused);
                break;
            default:
                break;
        }
    }
    /**
     * 再生状態へ遷移
     */
    setPlaying() {
        this.setStatus(VideoPlayerStatus.Playing);
    }
    /**
     * 終了状態へ遷移
     */
    setFinished() {
        this.setStatus(VideoPlayerStatus.Finished);
    }
    setCurrentTime(time) {
        this.currentTime = time;
    }
    reset() {
        this.setStatus(VideoPlayerStatus.Waiting);
        this.currentTime = 0;
        this.seekFromTime = 0;
    }
}
/**
 * VideoEventをハンドルして動画再生統計情報を計算
 */
export class VideoPlayAnalyzer {
    constructor() {
        Object.defineProperty(this, "completed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "pauseCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "playDuration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "track", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new VideoPlayTrack()
        });
        Object.defineProperty(this, "seekedEvents", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
    }
    onPaused() {
        this.pauseCount++;
    }
    onSeeked(event) {
        this.seekedEvents.push(event);
    }
    onPlaying(event) {
        const from = event.range.from;
        const to = event.range.to;
        this.playDuration += to - from;
        const fromFloor = Math.floor(from);
        const toFloor = Math.floor(to);
        range(fromFloor, toFloor + 1).forEach(() => {
            this.track.setPlayedAt(from);
        });
    }
    onCompleted() {
        this.completed = true;
    }
    setDuration(duration) {
        this.track.setDuration(duration);
    }
    get seekRanges() {
        return this.seekedEvents.map((e) => {
            return e.range;
        });
    }
    get totalSeekDuration() {
        return this.seekRanges.reduce((total, r) => {
            return total + Math.abs(r.to - r.from);
        }, 0);
    }
    get stats() {
        return {
            duration: this.track.duration || 0,
            pauseCount: this.pauseCount,
            playDuration: this.playDuration,
            dedupedPlayDuration: this.track.playedDuration,
            playDurationRatio: this.track.playedRatio,
            totalSeekDuration: this.totalSeekDuration,
        };
    }
}
/**
 * 動画のどの部分を再生したかの軌跡を計算する
 * 動画の時間軸を1secごとのVideoTimeSegmentに分割して、
 * それぞれのセグメントの再生有無を保持
 */
export class VideoPlayTrack {
    constructor() {
        Object.defineProperty(this, "duration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        }); // 動画全体の長さ
        Object.defineProperty(this, "segments", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        }); // セグメント配列
    }
    getSegmentAt(time) {
        const _time = Math.floor(time);
        let segment = this.segments[_time];
        if (!segment) {
            segment = new VideoTimeSegment(_time);
            this.segments[_time] = segment;
        }
        return segment;
    }
    setPlayedAt(time) {
        const segment = this.getSegmentAt(time);
        if (segment) {
            segment.played();
        }
    }
    /**
     * 動画全体の長さをセット
     */
    setDuration(length) {
        this.duration = length;
    }
    /**
     * 重複排除再生時間を取得
     * 再生したセグメント × 1sec = 再生時間として計算
     */
    get playedDuration() {
        const played = this.segments
            .filter((s) => !!s)
            .reduce((total, s) => {
            return s.hasPlayed ? total + 1 : total;
        }, 0);
        if (this.duration && played > this.duration) {
            return this.duration;
        }
        return played;
    }
    /**
     * 全体のうち再生した時間の割合を返す
     */
    get playedRatio() {
        if (!this.duration || this.duration === 0) {
            return 0;
        }
        return this.playedDuration / this.duration;
    }
}
export class VideoTimeSegment {
    constructor(time) {
        Object.defineProperty(this, "time", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_hasPlayed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        this.time = time;
    }
    get hasPlayed() {
        return this._hasPlayed;
    }
    played() {
        this._hasPlayed = true;
    }
}
